import {
    currentIdentity,
    Identity,
    onIdentityChange,
    useIdentity,
} from "@/utils/identity";
import { getDefaultStore } from "jotai";
import { Store } from "jotai/vanilla/store";
import { useEffect, useRef } from "react";

function useSocket<T>(
    channel: string,
    event: string,
    callback: (data: T) => void,
) {
    const cb = useRef(callback);
    cb.current = callback;
    useEffect(() => {
        window.Echo.private(channel).listen(event, cb.current);
        return () => {
            window.Echo.leaveChannel(channel);
        };
    }, []);
}

export function onLoggedOutWebsocketEvent<T>(
    token: string | null,
    eventName: string,
    callback: (data: T) => void,
) {
    const channel = `LoggedOutWebsocketChannel.${token}`;
    const cb = useRef(callback);
    cb.current = callback;
    useEffect(() => {
        if (!token) return;
        window.Echo.private(channel).listen(eventName, (event: T) => {
            cb.current(event);
        });
        return () => {
            window.Echo.leaveChannel(channel);
        };
    }, [token]);
}

type ChatEvent =
    | "ChatMessageCreated"
    | "ChatMessageUpdated"
    | "ChatMessageDeleted";

export function onChatEvent<T>(
    chatId: number,
    event: ChatEvent,
    callback: (data: T) => void,
) {
    const channel = `App.Models.Chat.${chatId}`;
    return useSocket(channel, event, callback);
}

export const ALL_NOTIFICATIONS = "ALL_NOTIFICATIONS";

function getUserChannel(
    user?: null | { type: "client" | "coach"; id: number },
) {
    const type = {
        client: "Client",
        coach: "Coach",
        none: "Unauthenticated",
    }[user?.type ?? "none"];
    return `App.Models.${type}.${user?.id ?? "Unauthenticated"}`;
}

export function onUserNotification<T>(
    notificationName: string,
    callback: (data: T) => void,
) {
    const { user, twoFactorPassed } = useIdentity();
    const type =
        user?.type === "client"
            ? "Client"
            : user?.type === "coach"
              ? "Coach"
              : "Unauthenticated";
    const channel = `App.Models.${type}.${user?.id ?? "Unauthenticated"}`;
    const cb = useRef(callback);
    cb.current = callback;

    useEffect(() => {
        if (!user || !twoFactorPassed) return;
        window.Echo.private(channel).notification(
            (notification: { type: string; id: string }) => {
                if (
                    notification.type === notificationName ||
                    notificationName === ALL_NOTIFICATIONS
                ) {
                    cb.current(notification as T);
                }
            },
        );
        return () => {
            window.Echo.leaveChannel(channel);
        };
    }, [user?.type, twoFactorPassed]);
}

export function onUserEvent<T>(eventName: string, callback: (data: T) => void) {
    const { user, twoFactorPassed } = useIdentity();
    const channel = getUserChannel(user);
    const cb = useRef(callback);
    cb.current = callback;

    useEffect(() => {
        if (!user || !twoFactorPassed) return;
        const listener = (event: { type: string; id: string }) => {
            cb.current(event as T);
        };
        window.Echo.private(channel).listen(eventName, listener);
        return () => {
            window.Echo.private(channel).stopListening(eventName, listener);
        };
    }, [user?.type, twoFactorPassed]);
}

/**
 * Used when you want to listen to events outside of the React lifecycle
 * to update Jotai atoms.
 */
function signedInUser({ user, twoFactorPassed }: Identity) {
    return user && twoFactorPassed ? { type: user.type, id: user.id } : null;
}

export function onJotaiUserEvent<T>(
    eventName: string,
    callback: ({
        store,
        identity,
        data,
    }: {
        store: Store;
        identity: { type: "client" | "coach"; id: number };
        data: T;
    }) => void,
    onLogout?: (store: Store) => void,
) {
    const store = getDefaultStore();
    let stopListening: (() => void) | null = null;
    let listeningAs: string | null | undefined;
    const handleIdentityChange = () => {
        const identity = signedInUser(currentIdentity());
        const identityKey = identity ? `${identity.type}.${identity.id}` : null;
        if (identityKey === listeningAs) {
            return;
        }
        listeningAs = identityKey;

        stopListening?.();
        stopListening = null;
        if (identity) {
            const channel = window.Echo.private(getUserChannel(identity));
            const listener = (data: T) => callback({ store, identity, data });
            channel.listen(eventName, listener);
            stopListening = () => channel.stopListening(eventName, listener);
        } else {
            onLogout?.(store);
        }
    };
    handleIdentityChange();
    onIdentityChange(handleIdentityChange);
}

export function onScheduleChange({
    clientId,
    callback,
}: {
    clientId?: number;
    callback: () => void;
}) {
    const { user, twoFactorPassed } = useIdentity();
    const cb = useRef(callback);
    cb.current = callback;

    const handleNotification = (notification: {
        type: string;
        id: string;
        client_id?: number;
    }) => {
        if (
            user?.type === "coach" &&
            clientId !== undefined &&
            notification.client_id !== clientId
        ) {
            return;
        }
        const scheduleNotificationTypes = [
            "App\\Notifications\\Appointments\\AppointmentCreatedByClientNotificationForClient",
            "App\\Notifications\\Appointments\\AppointmentCreatedByCoachNotificationForClient",
            "App\\Notifications\\Appointments\\AppointmentDeletedByClientNotificationForClient",
            "App\\Notifications\\Appointments\\AppointmentDeletedByCoachNotificationForClient",
            "App\\Notifications\\Appointments\\AppointmentCreatedByClientNotificationForCoach",
            "App\\Notifications\\Appointments\\AppointmentCreatedByCoachNotificationForCoach",
            "App\\Notifications\\Appointments\\AppointmentDeletedByClientNotificationForCoach",
            "App\\Notifications\\Appointments\\AppointmentDeletedByCoachNotificationForCoach",
            "ClientCoachScheduleMultipleChangedEvent",
            "AppointmentRescheduled",
        ];
        if (scheduleNotificationTypes.includes(notification.type)) {
            cb.current();
        }
    };

    useEffect(() => {
        if (!user || !twoFactorPassed) return;
        const channel = `App.Models.${user.type === "client" ? "Client" : "Coach"}.${user.id}`;
        window.Echo.private(channel).notification(handleNotification);
        return () => {
            window.Echo.leaveChannel(channel);
        };
    }, [user?.type, twoFactorPassed]);
}
